import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import FileDownloadRounded from '@mui/icons-material/FileDownloadRounded';
import exportRowsToXlsx from '../utils/exportXlsx';
import RoomPreferencesOutlined from '@mui/icons-material/RoomPreferencesOutlined';
import PageHeader from '../components/common/PageHeader';
import {
  getSettings,
  createSetting,
  deleteSetting,
} from '../services/settingsApi';

const Settings = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const data = await getSettings();
      setItems(data.items);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    if (!newName.trim()) {
      setError('Name is required');
      return;
    }
    setCreating(true);
    setError('');
    try {
      await createSetting(newName.trim());
      setDialogOpen(false);
      setNewName('');
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.message;
      setError(msg || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this setting? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await deleteSetting(id);
      await fetchData();
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setDeleting(null);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
      const existingNames = new Set(items.map((i) => i.name.toLowerCase()));
      let created = 0, skipped = 0;
      for (let i = 1; i < rows.length; i++) {
        const name = String(rows[i][0] || '').trim();
        if (!name || existingNames.has(name.toLowerCase())) { skipped++; continue; }
        await createSetting(name);
        existingNames.add(name.toLowerCase());
        created++;
      }
      alert(`Import complete! ${created} created, ${skipped} skipped (empty or duplicate)`);
      await fetchData();
    } catch (err) {
      console.error('Import failed:', err);
      alert(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Settings" subtitle="Manage usage area settings (e.g. Banquet, Bar, Restaurant)" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Settings"
        subtitle="Manage usage area settings (e.g. Banquet, Bar, Restaurant)"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <input id="settings-import-file" type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleImport} />
            <Button variant="outlined" startIcon={<UploadFileRounded />}
              onClick={() => document.getElementById('settings-import-file')?.click()}
              disabled={importing}>
              {importing ? 'Importing...' : 'Import Excel'}
            </Button>
            <Button variant="outlined" startIcon={<FileDownloadRounded />}
              onClick={() => exportRowsToXlsx({
                rows: items.map((i) => ({ name: i.name })),
                columns: ['name'],
                filename: 'settings',
                sheetName: 'Settings',
              })}
              disabled={items.length === 0}>
              Export
            </Button>
            <Button variant="contained" startIcon={<AddRounded />} onClick={() => setDialogOpen(true)}>
              Add Setting
            </Button>
          </Box>
        }
      />

      {items.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <RoomPreferencesOutlined sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No settings added
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Add Setting" to create your first usage area
            </Typography>
          </Box>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 160 }}>
                    Name
                  </TableCell>
                  <TableCell align="center" sx={{ width: 50 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(item.id)}
                          disabled={deleting === item.id}
                          sx={{ color: 'error.main' }}
                        >
                          {deleting === item.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <DeleteOutlineRounded fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Add Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setError(''); setNewName(''); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add Setting</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Name"
            placeholder="e.g. Banquet, Bar, Restaurant, Lobby"
            value={newName}
            onChange={(e) => { setNewName(e.target.value); setError(''); }}
            fullWidth
            sx={{ mt: 1 }}
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDialogOpen(false); setError(''); setNewName(''); }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
